import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useMotionValue, useSpring } from 'framer-motion';
import {
  ArrowRight, Eye, ShieldOff, Users, Star, ChevronRight, Megaphone, MapPin, CalendarCheck,
  CheckCircle2, Utensils, Dumbbell, Wrench, Store, ChevronDown, ChevronRight as ChevronR,
  Shield, CreditCard, Clock, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO, { localBusinessSchema, serviceSchema, faqSchema } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = `${API_URL}/api/static/images/hero-trailer.jpg`;
const MOCKUP_BAKERY = `${API_URL}/api/static/mockups/mockup-bakery.png`;
const MOCKUP_RESTAURANT = `${API_URL}/api/static/mockups/mockup-restaurant.png`;
const MOCKUP_FITNESS = `${API_URL}/api/static/mockups/mockup-fitness.png`;
const MOCKUP_HANDWERK = `${API_URL}/api/static/mockups/mockup-handwerk.png`;

/* ═══ Animated Counter ═══ */
const AnimatedCounter = ({ target, suffix = '', prefix = '', duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, { duration: duration * 1000, bounce: 0 });
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (isInView) motionVal.set(target);
  }, [isInView, target, motionVal]);

  useEffect(() => {
    const unsub = spring.on('change', (v) => {
      setDisplay(Math.round(v).toLocaleString('de-CH'));
    });
    return unsub;
  }, [spring]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
};

/* ═══ Fade variants ═══ */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const HomePage = () => {
  const [stats, setStats] = useState({ locations: 0 });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    fetch(`${API_URL}/api/locations`).then(r => r.json()).then(d => setStats({ locations: d.length || 0 })).catch(() => {});
  }, []);

  const faqs = [
    { question: 'Wie funktioniert die Buchung?', answer: 'Wählen Sie einen Standort, legen Sie den Zeitraum fest und buchen Sie online. Die Bezahlung erfolgt sicher über Stripe. Fertig.' },
    { question: 'Was kostet eine Werbefläche?', answer: 'Ab CHF 350 pro Woche. Der Preis hängt vom Standort und der Passantenfrequenz ab. Alle Preise sind transparent – keine versteckten Kosten.' },
    { question: 'Kann ich mein eigenes Plakat mitbringen?', answer: 'Ja! Sie können Ihren eigenen Druck mitbringen oder unseren Druckservice nutzen. Die exakten Druckmasse erhalten Sie bei der Buchung.' },
    { question: 'Wie viele Menschen sehen meine Werbung?', answer: 'Je nach Standort erreichen Sie täglich 5\'000 bis 15\'000 Autofahrer und Fussgänger. Die genaue Verkehrsfrequenz wird bei jedem Standort angezeigt.' },
    { question: 'Wie schnell ist meine Werbung sichtbar?', answer: 'Nach Buchungsbestätigung stellen wir den Anhänger in der Regel innerhalb von 1-2 Werktagen auf. Ihre Werbung wirkt sofort.' },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div data-testid="homepage">
      <SEO canonicalPath="/" structuredData={[localBusinessSchema(), serviceSchema(), faqSchema(faqs)]} />

      {/* ═══════════ HERO ═══════════ */}
      <section ref={heroRef} className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Parallax background */}
        <motion.div style={{ y: heroY }} className="absolute inset-0 -top-20">
          <div className="absolute inset-0 bg-[hsl(222,47%,11%)]" />
          <img
            src={HERO_IMG}
            alt="Mobiler Werbeanhänger"
            className="w-full h-[110%] object-cover opacity-30"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(222,47%,11%)] via-[hsl(222,47%,11%)/0.85] to-transparent" />
          {/* Noise overlay */}
          <div className="absolute inset-0 noise-overlay opacity-40" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left Copy */}
            <motion.div
              className="lg:col-span-7"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-sm font-medium mb-8 border border-white/10">
                <MapPin className="w-4 h-4 text-[#10B981]" />
                Zürcher Oberland
              </motion.span>

              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.08] text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Mehr Kunden für Ihr Geschäft –{' '}
                <span className="bg-gradient-to-r from-[#60A5FA] to-[#34D399] bg-clip-text text-transparent">dort, wo sie täglich vorbeigehen</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg text-white/70 mb-8 max-w-xl leading-relaxed">
                Ihre Werbung auf mobilen Anhängern an den meistbefahrenen Strassen der Region. Nicht wegklicken, nicht überscrollen – einfach sichtbar. Jeden Tag.
              </motion.p>

              {/* Animated Stats Row */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-6 sm:gap-10 mb-10">
                {[
                  { value: 15000, suffix: '+', label: 'Sichtkontakte / Tag' },
                  { prefix: 'CHF ', value: 350, label: 'pro Woche' },
                  { value: 48, suffix: 'h', label: 'bis Ihre Werbung steht' },
                ].map((s, i) => (
                  <div key={i} className="text-left">
                    <p className="text-3xl sm:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      <AnimatedCounter target={s.value} suffix={s.suffix || ''} prefix={s.prefix || ''} />
                    </p>
                    <p className="text-xs text-white/50 uppercase tracking-[0.15em] mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{s.label}</p>
                  </div>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
                <Link to="/locations">
                  <Button size="lg" className="w-full sm:w-auto text-base bg-white text-[hsl(222,47%,11%)] hover:bg-white/90 font-semibold shadow-lg shadow-white/10" data-testid="home-hero-primary-cta">
                    Standort finden
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#fuer-wen">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto text-base border-white/20 text-white hover:bg-white/10" data-testid="home-hero-secondary-cta">
                    Ist das was für mich?
                    <ChevronDown className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            {/* Right: Floating price card */}
            <motion.div
              className="lg:col-span-5 hidden lg:block"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1D5CA5]/20 to-[#10B981]/20 rounded-3xl blur-2xl" />
                <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/15 p-8 shadow-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Premium Standorte</p>
                      <p className="text-white/50 text-xs">Zürcher Oberland</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {[
                      { icon: Eye, text: 'Bis 15\'000 Sichtkontakte / Tag', color: '#60A5FA' },
                      { icon: Clock, text: 'In 48h aufgestellt', color: '#FBBF24' },
                      { icon: Shield, text: 'Keine versteckten Kosten', color: '#10B981' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-white/80 text-sm">
                        <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                        {item.text}
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Werbefläche ab</p>
                    <p className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      CHF 350<span className="text-lg font-normal text-white/50"> / Woche</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-1.5">
            <div className="w-1.5 h-3 rounded-full bg-white/40" />
          </div>
        </motion.div>
      </section>

      {/* ═══════════ REGIONAL CLAIM ═══════════ */}
      <section className="relative py-6 bg-gradient-to-r from-[hsl(222,47%,11%)] to-[hsl(222,47%,16%)] overflow-hidden" data-testid="regional-claim">
        <div className="absolute inset-0 noise-overlay opacity-20" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-white">
            <MapPin className="w-5 h-5 text-[#10B981]" />
            <span className="text-base sm:text-lg font-semibold tracking-wide" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Vom Züri Oberland, fürs Züri Oberland
            </span>
            <MapPin className="w-5 h-5 text-[#10B981]" />
          </div>
        </div>
      </section>

      {/* ═══════════ KONZEPT: VEREINE & ZO-FIRMEN ═══════════ */}
      <section className="py-20 lg:py-28 bg-[hsl(var(--surface-2))]" data-testid="concept-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp} className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Unser Konzept</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Regionale Werbung — fair, sichtbar, vernetzt
            </h2>
            <p className="text-[hsl(var(--muted-foreground))] text-lg max-w-2xl mx-auto">
              Wir glauben an die Region. Deshalb erhalten Zürcher Oberländer Firmen und Vereine besondere Konditionen.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {/* External */}
            <motion.div variants={fadeUp} custom={0}>
              <div className="relative h-full p-8 rounded-2xl bg-white border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-14 h-14 rounded-2xl bg-[#3B82F6]/10 flex items-center justify-center mb-6">
                  <Megaphone className="w-7 h-7 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-bold mb-2">Externe Kunden</h3>
                <p className="text-3xl font-bold text-[#3B82F6] mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>100%</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                  Voller Preis für alle Unternehmen ausserhalb des Zürcher Oberlandes. Profitieren Sie von Top-Standorten mit hoher Frequenz.
                </p>
                <ul className="space-y-2">
                  {['Alle Standorte verfügbar', 'Flexible Laufzeiten ab 1 Woche', 'Optionaler Druckservice'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* ZO-Firmen */}
            <motion.div variants={fadeUp} custom={1}>
              <div className="relative h-full p-8 rounded-2xl bg-white border-2 border-[#10B981] transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#10B981] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                  Empfohlen
                </div>
                <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mb-6">
                  <MapPin className="w-7 h-7 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-bold mb-2">ZO-Firmen</h3>
                <p className="text-3xl font-bold text-[#10B981] mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>50%</p>
                <p className="text-xs text-[#10B981] font-semibold mb-3">Rabatt für regionale Firmen</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                  Firmen mit Sitz im Zürcher Oberland erhalten nach Verifizierung im Handelsregister 50% Rabatt auf alle Standardflächen.
                </p>
                <ul className="space-y-2">
                  {['50% auf alle Standardflächen', 'Verifizierung via Handelsregister', 'Firmensitz im Zürcher Oberland', 'Gleiche Premium-Standorte'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Vereine */}
            <motion.div variants={fadeUp} custom={2}>
              <div className="relative h-full p-8 rounded-2xl bg-white border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-14 h-14 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-[#F59E0B]" />
                </div>
                <h3 className="text-lg font-bold mb-2">Vereine</h3>
                <p className="text-3xl font-bold text-[#F59E0B] mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Gratis</p>
                <p className="text-xs text-[#F59E0B] font-semibold mb-3">Nur Druckkosten</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                  Lokale Vereine erhalten 1 Woche pro Jahr kostenlos. Nur die Druckkosten werden verrechnet. Wir unterstützen die Region.
                </p>
                <ul className="space-y-2">
                  {['1 Gratis-Woche pro Jahr', 'Nur Druckkosten (ab CHF 150)', 'Begrenztes Kontingent', 'Kunden helfen Vereine unterstützen'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-14">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              Sind Sie eine ZO-Firma oder ein Verein? Registrieren Sie sich und verifizieren Sie Ihre Firma im Handelsregister.
            </p>
            <Link to="/auth">
              <Button size="lg" className="text-base" data-testid="concept-register-cta">
                Jetzt registrieren & profitieren <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ RESULTS ═══════════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="results-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp} className="mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Ergebnisse</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Was Ihre Werbung bewirkt</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-lg max-w-2xl">
              Sie kaufen keinen Anhänger – Sie kaufen Aufmerksamkeit, Neukunden und Umsatz.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          >
            {[
              { metric: 15000, prefix: '', suffix: '+', unit: 'Sichtkontakte / Tag', title: 'Maximale Sichtbarkeit', desc: 'Täglich sehen tausende Autofahrer und Fussgänger Ihre Werbung. Kein Algorithmus, keine Ad-Blocker – einfach da.', icon: Eye, color: '#1D5CA5' },
              { metric: 100, prefix: '', suffix: '%', unit: 'lokale Reichweite', title: 'Lokale Laufkundschaft', desc: 'Ihre Werbung steht genau dort, wo Ihre Zielgruppe lebt und einkauft. Kunden, die Sie sonst nie erreicht hätten.', icon: Users, color: '#10B981' },
              { metric: 0, prefix: '', suffix: '', unit: 'Konkurrenz', title: 'Null Konkurrenz daneben', desc: 'Anders als bei Google oder Instagram: Keine Werbung direkt darüber oder darunter. Ihre Botschaft – exklusiv.', icon: ShieldOff, color: '#F59E0B', displayText: '0' },
            ].map((b, i) => (
              <motion.div key={i} variants={fadeUp} custom={i}>
                <div className="group relative h-full p-8 rounded-2xl bg-white border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-[0_20px_50px_rgba(15,23,42,0.08)] hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${b.color}12` }}>
                    <b.icon className="w-7 h-7" style={{ color: b.color }} />
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: b.color }}>
                      {b.displayText !== undefined ? b.displayText : <AnimatedCounter target={b.metric} prefix={b.prefix} suffix={b.suffix} />}
                    </span>
                    <span className="text-sm text-[hsl(var(--muted-foreground))] ml-2">{b.unit}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{b.title}</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════ VISUAL PROOF ═══════════ */}
      <section className="py-20 lg:py-28 bg-[hsl(var(--surface-2))]" data-testid="visual-proof-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}>
              <motion.span variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                So könnte Ihre Werbung aussehen
              </motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Ihre Werbung – gross, sichtbar, direkt an der Strasse
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[hsl(var(--muted-foreground))] text-lg mb-8 max-w-lg">
                So wirkt Werbung auf unseren Anhängern: Ob Bäckerei, Restaurant, Fitness oder Handwerk – Ihr Plakat fällt auf. 24 Stunden am Tag, 7 Tage die Woche.
              </motion.p>
              <motion.div variants={fadeUp} className="space-y-4 mb-10">
                {[
                  'Grossformatige Werbefläche (bis 8m²)',
                  'LED-Beleuchtung für Sichtbarkeit auch nachts',
                  'Wetterfest bei Regen, Schnee und Sonne',
                  'Optionaler Druckservice – wir kümmern uns um alles',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                    </div>
                    <span className="text-sm font-medium">{item}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp}>
                <Link to="/locations">
                  <Button size="lg" className="text-base" data-testid="visual-proof-cta">Standorte & Preise ansehen <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {[
                  { src: MOCKUP_BAKERY, alt: 'Bäckerei-Werbung', h: 'h-52' },
                  { src: MOCKUP_RESTAURANT, alt: 'Restaurant-Werbung', h: 'h-52' },
                  { src: MOCKUP_FITNESS, alt: 'Fitnessstudio-Werbung', h: 'h-32' },
                  { src: MOCKUP_HANDWERK, alt: 'Handwerker-Werbung', h: 'h-32' },
                ].map((img, i) => (
                  <div key={i} className="group rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={img.src}
                      alt={`Werbeanhänger mit ${img.alt}`}
                      className={`w-full ${img.h} object-cover transition-transform duration-500 group-hover:scale-105`}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              <div className="absolute -bottom-5 -right-5 bg-[hsl(var(--primary))] text-white p-5 rounded-2xl shadow-xl max-w-[220px]">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-5 h-5" />
                  <span className="text-sm font-bold">Bis 15'000</span>
                </div>
                <p className="text-xs opacity-80">Kontakte / Tag an Top-Standorten</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════ USE CASES ═══════════ */}
      <section id="fuer-wen" className="py-20 lg:py-28 bg-white" data-testid="use-cases-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp} className="mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Für wen?</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Perfekt für lokale Unternehmen</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-lg max-w-2xl">
              Ob Restaurant, Handwerker oder Fitnessstudio – wenn Ihre Kunden in der Region leben, ist das Ihre Werbung.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {[
              {
                icon: Utensils, title: 'Gastronomie & Hotellerie', image: MOCKUP_RESTAURANT, accent: '#EF4444',
                problem: 'Neue Gäste finden, die noch nicht wissen, dass es Sie gibt.',
                solution: 'Werbeanhänger an Hauptstrassen in der Nähe Ihres Restaurants.',
                result: 'Mehr Laufkundschaft und Tischreservierungen durch lokale Sichtbarkeit.',
              },
              {
                icon: Wrench, title: 'Handwerk & Dienstleistung', image: MOCKUP_HANDWERK, accent: '#3B82F6',
                problem: 'Aufträge kommen nur über Mundpropaganda – zu langsam.',
                solution: 'Ihr Name und Ihre Telefonnummer grossflächig an der Hauptstrasse.',
                result: 'Kunden rufen direkt an, weil sie Ihre Werbung auf dem Arbeitsweg sehen.',
              },
              {
                icon: Dumbbell, title: 'Fitness & Gesundheit', image: MOCKUP_FITNESS, accent: '#10B981',
                problem: 'Google Ads sind teuer, Social Media wird ignoriert.',
                solution: 'Probetraining-Angebot auf Anhänger direkt vor der Zielgruppe.',
                result: 'Mehr Probetrainings und Neumitglieder durch tägliche Sichtbarkeit.',
              },
              {
                icon: Store, title: 'Detailhandel & Gewerbe', image: MOCKUP_BAKERY, accent: '#F59E0B',
                problem: 'Kunden fahren vorbei, ohne zu wissen, was Sie anbieten.',
                solution: 'Auffälliges Plakat mit Ihrem Angebot an der Durchfahrtsstrasse.',
                result: 'Mehr Spontanbesuche und höherer Umsatz durch regionale Bekanntheit.',
              },
            ].map((uc, i) => (
              <motion.div key={i} variants={fadeUp} custom={i}>
                <div className="group relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-xl border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-[0_20px_50px_rgba(15,23,42,0.1)] hover:-translate-y-1 h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img src={uc.image} alt={uc.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-5 flex items-center gap-2.5 text-white">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${uc.accent}30` }}>
                        <uc.icon className="w-4 h-4" />
                      </div>
                      <span className="font-semibold text-sm">{uc.title}</span>
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    {[
                      { label: 'Problem', color: '#EF4444', bg: 'rgba(239,68,68,0.08)', text: uc.problem },
                      { label: 'Lösung', color: '#1D5CA5', bg: 'rgba(29,92,165,0.08)', text: uc.solution },
                      { label: 'Ergebnis', color: '#10B981', bg: 'rgba(16,185,129,0.08)', text: uc.result },
                    ].map((row, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded mt-0.5 flex-shrink-0" style={{ color: row.color, background: row.bg }}>{row.label}</span>
                        <p className={`text-sm ${j === 2 ? 'font-medium text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))]'}`}>{row.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <section id="so-funktionierts" className="py-20 lg:py-28 bg-[hsl(222,47%,11%)] text-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp} className="mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#60A5FA] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>So einfach geht's</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>In 3 Schritten sichtbar</h2>
            <p className="text-white/60 text-lg">So einfach war Aussenwerbung noch nie.</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { step: '01', icon: MapPin, title: 'Standort wählen', desc: 'Auf der Karte stöbern, Verkehrsfrequenz und Preise vergleichen. Den perfekten Platz für Ihre Werbung finden.', color: '#60A5FA' },
              { step: '02', icon: CalendarCheck, title: 'Online buchen & bezahlen', desc: 'Zeitraum wählen, Verfügbarkeit prüfen, sicher bezahlen. Alles in wenigen Minuten erledigt.', color: '#34D399' },
              { step: '03', icon: Megaphone, title: 'Kunden gewinnen', desc: 'Wir stellen Ihren Anhänger auf. Ab sofort erreichen Sie täglich tausende Menschen in der Region.', color: '#FBBF24' },
            ].map((step, i) => (
              <motion.div key={i} variants={fadeUp} custom={i}>
                <div className="relative p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/8 hover:border-white/20 h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-5xl font-bold opacity-20" style={{ fontFamily: "'Space Grotesk', sans-serif", color: step.color }}>{step.step}</span>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${step.color}15` }}>
                      <step.icon className="w-6 h-6" style={{ color: step.color }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-14">
            <Link to="/locations">
              <Button size="lg" className="text-base bg-white text-[hsl(222,47%,11%)] hover:bg-white/90 font-semibold" data-testid="home-cta-how-it-works">
                Jetzt Standort finden <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ SOCIAL PROOF ═══════════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="social-proof-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats Row */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { value: stats.locations || 13, suffix: '+', label: 'Standorte', sub: 'im Zürcher Oberland' },
              { value: 15000, suffix: '+', label: 'Sichtkontakte/Tag', sub: 'an Top-Standorten' },
              { static: '24/7', label: 'Sichtbar', sub: 'bei jedem Wetter' },
              { value: 350, prefix: 'Ab ', label: 'CHF / Woche', sub: 'transparent & fair' },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} className="text-center p-8 rounded-2xl bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-md">
                <p className="text-3xl sm:text-4xl font-bold text-[hsl(var(--primary))] tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {s.static ? s.static : <AnimatedCounter target={s.value} prefix={s.prefix || ''} suffix={s.suffix || ''} />}
                </p>
                <p className="font-semibold mt-2 text-sm">{s.label}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{s.sub}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Testimonials */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Kundenstimmen</span>
              <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Was unsere Kunden sagen</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { quote: 'Seit wir unseren Werbeanhänger in Wetzikon stehen haben, kommen regelmässig Neukunden, die uns genau wegen dem Plakat gefunden haben.', name: 'Marco T.', role: 'Gastronomiebetrieb, Wetzikon', stars: 5 },
                { quote: 'Beste Investition fürs lokale Marketing. Die Buchung war einfach und der Support erstklassig. Innerhalb von 2 Tagen stand unser Plakat.', name: 'Sandra B.', role: 'Fitnessstudio, Uster', stars: 5 },
                { quote: 'Endlich eine Werbung, die nicht weggeklickt wird. Unsere Telefonnummer auf dem Anhänger bringt uns jede Woche neue Anfragen.', name: 'Peter K.', role: 'Handwerksbetrieb, Hinwil', stars: 5 },
              ].map((t, i) => (
                <motion.div key={i} variants={fadeUp} custom={i}>
                  <div className="h-full p-7 rounded-2xl bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] transition-all duration-300 hover:shadow-md">
                    <div className="flex gap-0.5 mb-4">
                      {[...Array(t.stars)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />)}
                    </div>
                    <blockquote className="text-sm leading-relaxed mb-6 text-[hsl(var(--foreground))]">"{t.quote}"</blockquote>
                    <div>
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="flex flex-wrap justify-center items-center gap-8 md:gap-14 mt-16 pt-10 border-t border-[hsl(var(--border))]"
          >
            {[
              { icon: MapPin, label: 'Schweizer Unternehmen' },
              { icon: CreditCard, label: 'Sichere Zahlung via Stripe' },
              { icon: CalendarCheck, label: 'Flexible Laufzeiten' },
              { icon: Phone, label: 'Persönliche Beratung' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 text-[hsl(var(--muted-foreground))]">
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.08)] flex items-center justify-center">
                  <item.icon className="w-4 h-4 text-[hsl(var(--primary))]" />
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════ FAQ ═══════════ */}
      <section className="py-20 lg:py-28 bg-[hsl(var(--surface-2))]" data-testid="faq-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp} className="mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-semibold mb-4 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Häufige Fragen</h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={staggerContainer}
            className="space-y-3"
          >
            {faqs.map((faq, i) => (
              <motion.div key={i} variants={fadeUp} custom={i}>
                <div
                  className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${openFaq === i ? 'border-[hsl(var(--primary)/0.3)] shadow-md' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.2)]'}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex items-center justify-between w-full p-6 text-left font-semibold hover:text-[hsl(var(--primary))] transition-colors"
                    data-testid={`faq-question-${i}`}
                  >
                    <span className="pr-4">{faq.question}</span>
                    <ChevronR className={`w-5 h-5 text-[hsl(var(--muted-foreground))] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-90' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{faq.answer}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-[hsl(var(--primary))]" />
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="max-w-3xl mx-auto text-center">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Bereit für mehr Kunden?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
              Finden Sie den perfekten Standort für Ihre Werbung. Die Buchung dauert nur wenige Minuten – Ihre Werbung wirkt den ganzen Tag.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/locations">
                <Button size="lg" className="bg-white text-[hsl(var(--primary))] hover:bg-white/90 text-base font-semibold w-full sm:w-auto shadow-lg" data-testid="home-cta-bottom">
                  Standorte ansehen <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/wettbewerb">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base w-full sm:w-auto" data-testid="home-cta-competition">
                  Gratis Werbefläche gewinnen
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
